
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import DashboardWrapper from "../../../components/DashboardWrapper";
import CrudForm from "../../../components/dashboard/form/CrudForm";
import bannerService from "../../../services/bannerService";

import {
  validateBannerImage,
  validateBannerTitle,
  validateBannerContent,
  validateStartingPrice,
} from "../../../utils/Validations";

export default function UpdateBanner() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);

  const localCacheKey = `bannerEditCache_${id}`;

  const allowedFields = [
    "banner",
    "banner_title",
    "banner_content",
    "starting_price",
    "status",
  ];

  // ==========================================
  // LOAD BANNER
  // ==========================================

  useEffect(() => {
    const loadBanner = async () => {
      try {
        setLoading(true);

        const res = await bannerService.getBannerById(id);
        const banner = res.data?.data || res.data;

        const normalized = {
          banner: banner.banner || null,
          banner_title: banner.banner_title || "",
          banner_content: banner.banner_content || "",
          starting_price: banner.starting_price ?? "",
          status: banner.status ?? 1,
        };

        setInitialData(normalized);

        localStorage.setItem(
          localCacheKey,
          JSON.stringify(normalized)
        );
      } catch (error) {
        console.error("Failed to load banner:", error);

        Swal.fire(
          "Error",
          "Failed to load banner details.",
          "error"
        );

        navigate("/admin/sliders");
      } finally {
        setLoading(false);
      }
    };

    const cached = localStorage.getItem(localCacheKey);

    if (cached) {
      setInitialData(JSON.parse(cached));
      setLoading(false);
    }

    loadBanner();
  }, [id, navigate, localCacheKey]);

  // ==========================================
  // HANDLE SUBMIT
  // ==========================================

  const handleSubmit = useCallback(
    async (formData) => {
      try {
        // ==========================================
        // GET NEW IMAGE
        // ==========================================

        const bannerFile = Array.isArray(formData.banner)
          ? formData.banner[0]
          : formData.banner;

        // ==========================================
        // 1. BANNER IMAGE
        // ==========================================
        // Existing image is allowed.
        // Validate only when user selects a NEW image.

        if (bannerFile instanceof File) {
          const bannerImageError =
            validateBannerImage(bannerFile);

          if (bannerImageError !== true) {
            Swal.fire(
              "Validation Error",
              bannerImageError,
              "warning"
            );
            return;
          }
        }

        // ==========================================
        // 2. BANNER TITLE
        // ==========================================

        const bannerTitleError = validateBannerTitle(
          formData.banner_title
        );

        if (bannerTitleError !== true) {
          Swal.fire(
            "Validation Error",
            bannerTitleError,
            "warning"
          );
          return;
        }

        // ==========================================
        // 3. BANNER CONTENT
        // ==========================================

        const bannerContentError = validateBannerContent(
          formData.banner_content
        );

        if (bannerContentError !== true) {
          Swal.fire(
            "Validation Error",
            bannerContentError,
            "warning"
          );
          return;
        }

        // ==========================================
        // 4. STARTING PRICE
        // ==========================================

        const startingPriceError = validateStartingPrice(
          formData.starting_price
        );

        if (startingPriceError !== true) {
          Swal.fire(
            "Validation Error",
            startingPriceError,
            "warning"
          );
          return;
        }

        // ==========================================
        // CREATE PAYLOAD
        // ==========================================

        setLoading(true);

        const payload = {};

        allowedFields.forEach((field) => {
          if (field === "banner") {
            // Only send a NEW image.
            if (bannerFile instanceof File) {
              payload.banner = bannerFile;
            }
          } else {
            payload[field] = formData[field] ?? "";
          }
        });

        console.log("Update payload:", payload);

        // ==========================================
        // API REQUEST
        // ==========================================

        await bannerService.updateBanner(id, payload);

        Swal.fire({
          title: "Success",
          text: "Banner updated successfully!",
          icon: "success",
          confirmButtonColor: "#164E4F",
        });

        localStorage.removeItem(localCacheKey);

        navigate("/admin/sliders");
      } catch (error) {
        console.error("Update banner error:", error);

        const message =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors)
                .flat()
                .join(", ")
            : error.response?.data?.message ||
              "Failed to update banner";

        Swal.fire("Error", message, "error");
      } finally {
        setLoading(false);
      }
    },
    [id, navigate, localCacheKey]
  );

  // ==========================================
  // LOADING
  // ==========================================

  if (loading) {
    return (
      <DashboardWrapper>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <div
            className="spinner-border text-primary"
            role="status"
          >
            <span className="visually-hidden">
              Loading...
            </span>
          </div>
        </div>
      </DashboardWrapper>
    );
  }

  // ==========================================
  // NO DATA
  // ==========================================

  if (!initialData) {
    return (
      <DashboardWrapper>
        <div className="alert alert-danger">
          Banner data not found.
        </div>
      </DashboardWrapper>
    );
  }

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <DashboardWrapper>
      <div className="p-4">
        <h1 className="h4 mb-4">Update Banner</h1>

        <button
          type="button"
          className="btn btn-secondary mb-3"
          onClick={() => navigate(-1)}
        >
          Back
        </button>

        <div className="card">
          <h5 className="card-header">Edit Banner</h5>

          <div className="card-body">
            <CrudForm
              initialData={initialData}
              onSubmit={handleSubmit}
              allowedFields={allowedFields}
              fileFields={[
                {
                  name: "banner",
                  multiple: false,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
